import { useEffect, useState } from "react";
import { URL } from "src/libs/interfaces/layouts";
import { LoaderBallsLine, LoaderLine, Select } from "src/libs/useful";
import { ISelectItem } from "src/libs/useful/select";
import IScalarForm, { SCALAR_TYPES, VerticalType } from "../interface/IScalarForm";
import Scalar from "./Scalar";
import addCSSClass from "src/libs/layout-app/utils/addCSSClass";
import removeCSSClass from "src/libs/layout-app/utils/removeCSSClass";
import { addCSSLink } from "src/libs/layout-app/utils/addCSSLink";
import FieldInput from "..";
import { __ } from "src/libs/utilities";
import { Tag } from "@blueprintjs/core";

/* Protopia Ecosystem component */
export interface IGoogleFontItem {
   family: string,
    variants: string[],
    subsets: FontSubsets
    version: string
    lastModified: string
    files: IFontFiles,
    category: FontCategory,
    "kind": "webfonts#webfont",
    "menu": URL
}
export enum FontSubsets {
    LATIN="latin",
    CYRILLIC="cyrillic",
    JAPANESE="japanese",
    LATIN_EXT="latin-ext",
}
export enum FontCategory {
    SANS_SERIF="sans-serif",
    SERIF="serif",
    DISPLAY="display",
    HANDWRITING="handwriting",
    MONOSPACE="monospace",
}
export enum IFontWeight {
    THIN="100",
    EXTRA_LIGHT="200",
    LIGHT="300",
    REGULAR="400",
    MEDIUM="500",
    SEMI_BOLD="600",
    BOLD="700",
    EXTRA_BOLD="800",
    BLACK="900",
}
export type IFontFiles = {
    [ key in IFontWeight ]: string
}

const GoogleFonts = (props: IScalarForm) : JSX.Element => {
    return <Scalar 
        { ...props } 
        notClear
        vertical={VerticalType.VERTICAL}
        enabledForm={<GoogleFontsEnabled {...props} notClear />}
        desabledForm={<GoogleFontsDisabled {...props} />}
    />
}
const FONT_COLORS =[
    "#2e80b8ff",
    "#266fa0ff",
    "#2b6185ff",
    "#113c58ff",
    "#254b64ff",
    "#0f598aff",
    "#1c75b1ff",
]

const GoogleFontsDisabled = (props: IScalarForm): JSX.Element => {
    return <div className="d-flex w-100 flex-wrap">
        { props.value?.map((font: string) => <Tag >{font}</Tag>)}
    </div>
}
const GoogleFontsEnabled = (props: IScalarForm): JSX.Element => {
    const [fonts, setFonts] = useState<IGoogleFontItem[]>([])
    const [isLoad, setIsLoad] = useState<boolean>(false) 
    const [value, setValue] = useState<string[]>([])
    const [filter, setFilter] = useState<FontCategory> ( FontCategory.SANS_SERIF )

    useEffect(() => {
        fetch("/assets/data/fonts.json")
            .then(( res: any ) => {                
                return res.json()
            })
            .then(( res: any ) => { 
                setFonts( res.items )
                setIsLoad( true ) 
            })
    }, [])
    useEffect(() => {
        if(Array.isArray(props.value)) {
            setValue( props.value )
            props.value.forEach( (v, i) => {
                const className = v.replaceAll(" " , "-")
                addCSSClass(
                    `font-${className}`,
                    `.font-${className} {
                        font-family: ${ v };
                        color: ${ FONT_COLORS[i % 6] };
                    }`
                )

            })
            addCSSLink( props.value )
        }
        return () => {
            if(Array.isArray(props.value)) {
                props.value.forEach( (v: string) => { removeCSSClass( `font-${ v.replaceAll(" " , "-") }` ) } )
            }
        }
    }, [ props.value ])
    
    if(!isLoad) {
        return <LoaderBallsLine />
    }
    
    const getTitledItem = ( i: number ) => { 
        if(!i) return {id:-1, title: "--"} 
        let item : IGoogleFontItem = fonts[i]
        return { ...item, title:  item.family, id: item.family }
    }
    const onChange = ( items: ISelectItem[] ) => { 
        const val = [...items].map(item => item.id.toString() )
        if(!!props.max) {
            setValue( val.filter((f, i) => i < ( props.max! )) )
        }
        else  {
            setValue( val )
        }
        
        if( props.onChange ) {
            props.onChange( val,  props.field, "" )
        }   
    }
    const onFilter = (cat: FontCategory) => {
        setFilter( cat )
    } 
    
    const selectItems = () : ISelectItem[] => fonts
        .filter((item, i) => item.subsets.includes( "cyrillic" ))
        .filter((item) => item.category.toString() === filter.toString())
        .map((item, i) => ({ id: item.family, title: item.family, key: item.family }))

    return <div className='pe-google-fonts-container w-100'>
        <div className="mb-2 px-3">
            <FieldInput
                type={SCALAR_TYPES.TAGS}
                value={filter}
                onChange={onFilter}
                editable
                notClear
                values={Object.values( FontCategory ).map(cat => {
                    return { _id: cat, title: __(cat) }
                })}
            />
        </div>
        <Select<ISelectItem>
            isMulti={ true }
            items={ [ { id: -1, title: "--", key: -1 }, ...selectItems() ] }
            value={ value.map(v => ({id:v, key:v, title:v})) }
            onChange={ onChange } 
            placeholder={props.placeholder || "Select fonts"}
        />
        <div className="py-2 d-flex flex-wrap">
            {
                ( value || [] ).map( v => {
                    return <span className={`m-2 display-5 font-${ v.replaceAll(" " , "-") }`} key={v}>
                        { v }
                    </span>
                } )
            }
        </div>
    </div>
}
export default GoogleFonts

